import type { Editor, Operation, Range } from 'slate';
import { Editor as SlateEditor, Transforms } from 'slate';

import { Queries } from '../../queries/queries-object';
import SuggestionMarkerTransforms from '../../transforms/suggestion-marker-transforms';
import type { ParagraphElement } from '../../types';
import { isParagraphElement, isSuggestionMarkerStartElement } from '../../types';

type AiSuggestionMarkerEditGroup = {
  authorId: string;
  endParagraphLocationId: string;
  groupId: string;
  startParagraphLocationId: string;
};

export function normalizeAiSuggestionMarkers(editor: Editor) {
  const editGroups = getAiSuggestionMarkerEditGroups(editor);

  insertMissingSuggestionMarkers(editor, editGroups);
  backfillSuggestionMarkerAuthorIds(editor, editGroups);
  removeOrphanedSuggestionMarkers(editor, editGroups);
}

/**
 * Removes any suggestion-marker pair whose suggestion no longer has edits in the
 * document (the start/end markers are left bracketing nothing). This runs live
 * on every editor-level normalize pass, and deliberately as a removal-only
 * normalizer, for three reasons:
 *
 * - Path-agnostic: a marker becomes empty no matter how its last edit was
 *   resolved — accept/reject from the suggestions toolbar OR the inline toolbar,
 *   manually deleting the edited text, a programmatic transform, or undo/redo.
 *   A normalizer cleans up all of those; wiring marker removal into each
 *   accept/reject handler would miss the manual-delete and programmatic paths.
 * - Correct undo: because this runs inside the normalize pass of the operation
 *   that emptied the marker, the removal is part of that operation's history
 *   entry, so undo restores the text, its edits, and its markers together. A
 *   debounced/standalone cleanup transform would split that into a separate,
 *   awkward undo step (or, if made non-undoable, could leave edits restored
 *   without their markers).
 * - Removal-only: re-inserting MISSING markers stays a migration / explicit-
 *   insert concern (see `normalizeAiSuggestionMarkers`); this rule only fixes
 *   the orphaned-marker case, which is the bug it exists for.
 *
 * Cost control, in two layers. First, a flush whose operations cannot remove a
 * suggested edit (plain typing is insert_text + set_selection) returns before
 * touching the document at all. Second, when the check does run, it costs two
 * document walks total — one for the marker pairs, one for the groupIds that
 * still have edits — instead of one walk per marker.
 */
export function removeEmptySuggestionMarkers(editor: Editor): void {
  if (!getCanOperationsRemoveSuggestedEdits(editor.operations)) return;

  const markers = Queries.getAllSuggestionMarkers(editor);
  if (markers.length === 0) return;

  const liveGroupIds = Queries.getAllSourcedSuggestionGroupIds(editor);
  for (const { start } of markers) {
    if (liveGroupIds.has(start.suggestionId)) continue;
    SuggestionMarkerTransforms.removeSuggestionMarkers(editor, start.suggestionId);
  }
}

/**
 * Op types that cannot remove a sourced suggested edit from the document.
 * insert_text and set_selection are plain typing; remove_text deletes
 * characters but never the `suggestedEdits` property that carries the edit —
 * if deleting empties a text node, its cleanup arrives as a separate
 * merge_node/remove_node op, which re-dirties the editor root and fails this
 * check on the same flush. This is an allowlist rather than a list of
 * edit-removing op types so that every other op — remove_node, merge_node,
 * set_node, and anything not considered here — errs toward running the scan.
 */
const EDIT_PRESERVING_OPERATION_TYPES = new Set<Operation['type']>([
  'insert_text',
  'remove_text',
  'set_selection',
]);

function getCanOperationsRemoveSuggestedEdits(operations: Operation[]): boolean {
  /**
   * An empty list means the pass wasn't triggered by edits — e.g. a forced
   * normalize on document load — which must still clean up any orphaned
   * markers the document was saved with.
   */
  if (operations.length === 0) return true;

  return !operations.every(operation => EDIT_PRESERVING_OPERATION_TYPES.has(operation.type));
}

function getAiSuggestionMarkerEditGroups(editor: Editor) {
  const editGroups = new Map<string, AiSuggestionMarkerEditGroup>();
  const paragraphEntries = SlateEditor.nodes<ParagraphElement>(editor, {
    at: [],
    match: isParagraphElement,
  });

  for (const [paragraph] of paragraphEntries) {
    if (!paragraph.locationId) continue;

    for (const source of Queries.getUniqSuggestionSources(paragraph)) {
      const existingGroup = editGroups.get(source.groupId);
      if (!existingGroup) {
        editGroups.set(source.groupId, {
          authorId: source.authorId,
          endParagraphLocationId: paragraph.locationId,
          groupId: source.groupId,
          startParagraphLocationId: paragraph.locationId,
        });
        continue;
      }

      existingGroup.endParagraphLocationId = paragraph.locationId;
    }
  }

  return [...editGroups.values()];
}

function insertMissingSuggestionMarkers(editor: Editor, editGroups: AiSuggestionMarkerEditGroup[]) {
  for (const group of editGroups) {
    const { start } = Queries.getSuggestionById(editor, group.groupId);
    if (start) continue;

    const range = getParagraphRangeForEditGroup(editor, group);
    if (!range) continue;

    SuggestionMarkerTransforms.insertSuggestionMarkers(editor, {
      at: range,
      authorId: group.authorId,
      reasoning: '',
      suggestionActionType: 'edit',
      suggestionId: group.groupId,
    });
  }
}

function backfillSuggestionMarkerAuthorIds(
  editor: Editor,
  editGroups: AiSuggestionMarkerEditGroup[]
) {
  for (const group of editGroups) {
    const { start } = Queries.getSuggestionById(editor, group.groupId);
    if (!start || start.authorId !== undefined) continue;

    const inferredAuthorId = Queries.inferAuthorIdForSuggestion(editor, group.groupId);
    if (!inferredAuthorId) continue;

    const startEntry = SlateEditor.nodes(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) && node.suggestionId === group.groupId,
    }).next().value;
    if (!startEntry) continue;

    Transforms.setNodes(editor, { authorId: inferredAuthorId }, { at: startEntry[1], voids: true });
  }
}

function removeOrphanedSuggestionMarkers(
  editor: Editor,
  editGroups: AiSuggestionMarkerEditGroup[]
) {
  const editGroupIds = new Set(editGroups.map(group => group.groupId));
  const markers = Queries.getAllSuggestionMarkers(editor);
  if (markers.length === 0) return;

  const liveGroupIds = Queries.getAllSourcedSuggestionGroupIds(editor);
  for (const marker of markers) {
    const groupId = marker.start.suggestionId;
    if (editGroupIds.has(groupId)) continue;
    if (liveGroupIds.has(groupId)) continue;

    SuggestionMarkerTransforms.removeSuggestionMarkers(editor, groupId);
  }
}

function getParagraphRangeForEditGroup(
  editor: Editor,
  group: AiSuggestionMarkerEditGroup
): Range | null {
  const startParagraphEntry = SlateEditor.nodes<ParagraphElement>(editor, {
    at: [],
    match: n => isParagraphElement(n) && n.locationId === group.startParagraphLocationId,
  }).next().value;
  if (!startParagraphEntry) return null;

  const [, startParagraphPath] = startParagraphEntry;
  if (group.startParagraphLocationId === group.endParagraphLocationId) {
    return {
      anchor: SlateEditor.start(editor, startParagraphPath),
      focus: SlateEditor.end(editor, startParagraphPath),
    };
  }

  const endParagraphEntry = SlateEditor.nodes<ParagraphElement>(editor, {
    at: [],
    match: n => isParagraphElement(n) && n.locationId === group.endParagraphLocationId,
  }).next().value;
  if (!endParagraphEntry) {
    return {
      anchor: SlateEditor.start(editor, startParagraphPath),
      focus: SlateEditor.end(editor, startParagraphPath),
    };
  }

  const [, endParagraphPath] = endParagraphEntry;
  return {
    anchor: SlateEditor.start(editor, startParagraphPath),
    focus: SlateEditor.end(editor, endParagraphPath),
  };
}
